import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { guardarTipoPuntoVentaService } from '../../../../service/catalogos/TipoPuntoVenta';
import { useObtenerTipoPorId } from '../hooks/TipoPuntoVentaHook';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  id_row: number;
};

const CreateModal: React.FC<Props> = ({ id_row, show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const { initValues } = useObtenerTipoPorId(id_row);

  const schema = Yup.object().shape({
    nombre: Yup.string().trim().required('El nombre es requerido.').max(50, 'El nombre no puede exceder 50 caracteres.'),
    codigo: Yup.string()
      .trim()
      .max(20, 'El código no puede exceder 20 caracteres.')
      .when('id', {
        is: (val: number) => !val || val === 0,
        then: Yup.string().trim().required('El código es requerido.'),
      }),
  });

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await guardarTipoPuntoVentaService(values);
        SimpleAlert(
          id_row ? 'Tipo actualizado exitosamente.' : 'Tipo creado exitosamente.',
          3000,
          'success'
        );
        handleClose(true);
        formik.resetForm();
      } catch (error: any) {
        const message = error?.response?.data?.message ?? 'Ocurrió un error.';
        SimpleAlert(message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      id="kt_modal_tipo_pv"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-500px"
      show={show}
      onHide={() => {
        formik.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>{id_row ? 'Editar tipo' : 'Nuevo tipo de punto de venta'}</h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formik.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form onSubmit={formik.handleSubmit}>
            {/* Código — editable al crear, solo lectura al editar */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className={`fw-bold fs-6 mb-2 ${!id_row ? 'required' : ''}`}>Código</label>
                {formik.touched.codigo && formik.errors.codigo && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.codigo}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Ej. PUESTO"
                {...formik.getFieldProps('codigo')}
                className="form-control"
                type="text"
                autoComplete="off"
                disabled={!!id_row}
                maxLength={20}
                style={{ textTransform: 'uppercase', backgroundColor: id_row ? '#f5f5f5' : undefined }}
                onChange={(e) =>
                  formik.setFieldValue('codigo', e.target.value.toUpperCase())
                }
              />
              {!id_row && (
                <small className="text-muted">El código es único e inmutable una vez creado.</small>
              )}
            </div>
            {/* Nombre */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Nombre</label>
                {formik.touched.nombre && formik.errors.nombre && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.nombre}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Nombre del tipo"
                {...formik.getFieldProps('nombre')}
                className="form-control"
                type="text"
                autoComplete="off"
                maxLength={50}
              />
            </div>
            {/* Activo — solo al editar */}
            {!!id_row && (
              <div className="fv-row mb-7">
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="chk_activo"
                    checked={formik.values.activo}
                    onChange={(e) => formik.setFieldValue('activo', e.target.checked)}
                  />
                  <label className="form-check-label fw-bold fs-6" htmlFor="chk_activo">
                    Activo
                  </label>
                </div>
              </div>
            )}
            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  formik.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {!loading ? (
                  <span>{id_row ? 'Actualizar' : 'Guardar'}</span>
                ) : (
                  <span style={{ display: 'block' }}>
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { CreateModal };
